﻿import TKSelectRedirect from '@tk/controls/tk.select.redirect';
import render from '@tk/utilities/tk.render';
import TCArtSpecTable from '@/components/tc.art.spec.table';

interface Attribute {
    attr: string;
    label: string;
    value?: string;
    sorttype: SortType;
}

interface ArticleVariant {
    id: string;
    attributes: Attribute[];
}

interface ArticleVariantsResponse {
    attributes: Attribute[];
    variants: ArticleVariant[];
}

type SortType = 'A' | 'N';

export default class TCSelectRedirect extends TKSelectRedirect {
    artSpecTable?: TCArtSpecTable;
    constructor() {
        super();

        this.artSpecTable = document.querySelector('[data-tk-artspectable]') || undefined;
    }

    async handleChangeSelection(event: InputEvent) {
        const target = event.target as HTMLSelectElement;
        const id = this.getIdFromMultipleSelection(target);
        if (!id) return;
        await this.getAsyncContentById(id);
        this.artSpecTable && this.artSpecTable.fetch(id);
        this.redirectByValue(id);
    }

    getIdFromMultipleSelection(currentFilter: HTMLSelectElement): string | undefined {
        const currentFilterId = this.selections.findIndex((selection) => selection.fieldElement === currentFilter);

        const attributes = this.selections.map((selection) => ({
            attr: selection.attribute,
            value: selection.fieldElement.value,
        })) as Attribute[];

        let matchResults = this.compareAttributes(attributes, this.variants as ArticleVariant[])
            .filter((variant) => variant.matches[currentFilterId] === true)
            .sort((a, b) => b.matchCount - a.matchCount)
            .filter((variant, _, arr) => variant.matchCount === arr[0].matchCount);

        if (matchResults.length === 0) {
            throw new Error('tc.select.redirect: no matches');
        }

        for (let index = 0; matchResults.length > 1; index += index) {
            if (index === currentFilterId) break;
            matchResults = matchResults.filter((variant) => variant.matches[index]);
        }

        return matchResults[0]?.id;
    }

    compareAttributes(firstArray: Attribute[], secondArray: ArticleVariant[]) {
        return secondArray.map((item) => {
            const matches = firstArray.map((ref) => {
                const match = item.attributes.find((attr) => attr.attr === ref.attr);
                return match ? match.value === ref.value : false;
            });

            return {
                id: item.id,
                matches,
                matchCount: matches.filter(Boolean).length,
            };
        });
    }

    handleVariantsResponse(response: TKResponse<ArticleVariantsResponse>) {
        if (!response || !response.success) return;
        const { variants, attributes } = response.dataAsJson;
        this.variants = variants;

        const variantDictionary = this.currentId ? this.getAllVariantsById(this.currentId) : undefined;
        const selectedAttributes = this.currentId ? this.getAttributesById(this.currentId) : undefined;

        if (!variantDictionary) return;
        attributes.forEach((item) => {
            const values = new Set<string>();
            variantDictionary[item.attr].forEach((value) => {
                values.add(value);
            });

            const selectedValue = selectedAttributes?.find((selectedItem) => selectedItem.attr === item.attr)?.value;

            const elements = this.createSelectbox({
                id: item.attr,
                label: item.label,
                values: Array.from(values),
                selectedValue,
                sort: item.sorttype as SortType,
            });

            if (!elements) return;

            this.selections.push({
                fieldElement: elements.fieldElement,
                wrapper: elements.wrapper,
                attribute: item.attr,
                label: item.label,
                values,
            });
        });

        this.innerHTML = '';
        this.selections.forEach((item) => {
            this.insertAdjacentElement('beforeend', item.wrapper);
        });
    }

    sortComparator(a: string, b: string, sortType: SortType = 'A'): number {
        if (sortType === 'N') {
            return Number(a) - Number(b);
        }

        // Standard: alphanumerisch
        return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });
    }

    /**
     * Creates a select box with the given configuration and attaches change event listener.
     *
     * @param {Object} params - The parameters for creating the select box.
     * @param {string} params.id - The ID for the select box element.
     * @param {string} params.label - The label for the select box element.
     * @param {string[]} params.values - The values to populate the select box options.
     * @param {string} [params.selectedValue] - The value to be selected by default, if any.
     * @returns {{ wrapper: HTMLElement, fieldElement: HTMLSelectElement } | undefined}
     * An object containing the wrapper and field element, or undefined if the field element is not found.
     */
    createSelectbox({
        id,
        label,
        values,
        selectedValue,
        sort,
    }: {
        id: string;
        label: string;
        values: string[];
        selectedValue?: string;
        sort: SortType;
    }):
        | {
              wrapper: HTMLElement;
              fieldElement: HTMLSelectElement;
          }
        | undefined {
        const validValues = Array.from(new Set(values.map((v) => v.trim()).filter((v): v is string => v.length > 0)));

        if (validValues.length <= 1) {
            return;
        }

        const wrapper = render(`
            <div class="tk-form-select">
                <label for="form-variants-${id}">${label}</label>
                <div class="tk-form__wrapper">
                    <select
                        id="form-variants-${id}"
                        name="form-variants-${id}"
                        data-tk-product-variants
                        sort="${sort}"
                    >
                        ${validValues
                            .sort((a, b) => this.sortComparator(a, b, sort))
                            .map((value) => {
                                const escapedValue = value.replace(/"/g, '&quot;');
                                return `<option value="${escapedValue}" ${selectedValue === value ? 'selected' : ''}>${value}</option>`;
                            })
                            .join('\n')}
                    </select>
                </div>
            </div>
        `);
        const fieldElement = wrapper.querySelector('select');
        if (!fieldElement) return;
        const onChange = this.handleChangeSelection.bind(this);
        this.pushListener({ event: 'input', element: fieldElement, action: onChange });
        return {
            wrapper,
            fieldElement,
        };
    }
}
